import { defineStore } from 'pinia'
import axios from '@/api/axios'
import { partnerLogoSrc, whatsappHref } from '@/utils/partnerBrand'

type BrandingState = {
  loaded: boolean
  name: string
  logo: string
  phone: string
}

let inflight: Promise<void> | null = null

function applyFavicon(url: string) {
  if (!url || typeof document === 'undefined') return
  document
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]')
    .forEach(node => {
      if (node instanceof HTMLLinkElement) node.href = url
    })
}

export const useBrandingStore = defineStore('branding', {
  state: (): BrandingState => ({
    loaded: false,
    name: '',
    logo: '',
    phone: '',
  }),
  getters: {
    logoUrl(state): string {
      return partnerLogoSrc(state.logo)
    },
    whatsappUrl(state): string {
      return whatsappHref(state.phone)
    },
  },
  actions: {
    async load() {
      if (this.loaded) return
      if (!inflight) {
        inflight = this.fetch().finally(() => {
          inflight = null
        })
      }
      await inflight
    },
    async fetch() {
      try {
        const { data } = await axios.get(
          '/partner/branding',
          // Branding is optional. Do not retry a down API before showing the logo.
          { 'axios-retry': { retries: 0 } } as Parameters<typeof axios.get>[1]
        )
        this.name = typeof data?.name === 'string' ? data.name : ''
        this.logo = typeof data?.logo === 'string' ? data.logo : ''
        this.phone = typeof data?.phone === 'string' ? data.phone : ''
        applyFavicon(partnerLogoSrc(this.logo))
      } catch {
        this.name = ''
        this.logo = ''
        this.phone = ''
      } finally {
        this.loaded = true
      }
    },
  },
})
